import { type ComponentPropsWithoutRef, forwardRef } from 'react';

import { cva, type VariantProps } from '../../utils/cva';

const loadingBarVariants = cva({
  base: ['overflow-hidden', 'h-[3px]'],
  variants: {
    /**
     * Controls the positioning behavior of the loading bar.
     * - `page`: Fixed positioning at the top of the viewport (for page-level loading)
     * - `inline`: Relative positioning within its container (for section-level loading)
     */
    variant: {
      page: ['fixed', 'inset-x-0', 'top-0', 'z-50'],
      inline: ['relative', 'w-full'],
    },
  },
  defaultVariants: {
    variant: 'page',
  },
});

export type LoadingBarProps = ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof loadingBarVariants> & {
    'data-testid': string | null;
  };

/**
 * An indeterminate loading bar that displays a sliding animation.
 * Used to indicate loading states such as navigation or data fetching.
 *
 * @example
 * ```tsx
 * // Page-level loading (fixed at top of viewport)
 * {isLoading && <LoadingBar data-testid="page-loading-bar" />}
 *
 * // Inline loading (within a container)
 * {isFetching && <LoadingBar variant="inline" data-testid="section-loading-bar" />}
 * ```
 */
export const LoadingBar = forwardRef<HTMLDivElement, LoadingBarProps>(
  (props, ref) => {
    const { className, variant, 'data-testid': dataTestId, ...rest } = props;

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-busy="true"
        // TODO: Translate this string
        aria-label="Loading"
        data-testid={dataTestId ?? 'loading-bar'}
        className={loadingBarVariants({ variant, className })}
        {...rest}
      >
        <span
          aria-hidden
          className="absolute left-0 top-0 h-full w-full pointer-events-none bg-primary origin-left animate-loading-bar-indeterminate"
        />
      </div>
    );
  },
);
